import React, { useCallback, useState } from 'react';
import { ButtonV2, Flex, TextV2 } from '@withjoy/joykit';
import { useTranslation } from '@shared/core';
import { useWindowFunction } from '@shared/hooks/useWindowFunction';
import {
  AllyPromoHeadline,
  AllyPromoSubtext,
  AllyAmountDisplay,
  AllyAmountSubtext,
  AllyNudgeBar,
  AllyNudgeText,
  AllyPrimaryButton,
  AllySecondaryButton,
  AllyFinePrint,
} from './TransferToBankDialog.styles';
import { ALLY_PURPLE } from './constants';

const ALLY_URL = 'https://www.ally.com/';

type AllyTransferState = 'ally' | 'existing';

export type AllyTransferPromoProps = Readonly<{
  allyAmountFormatted: string;
  standardAmountFormatted: string;
  feeFormatted: string;
  onConnectBank?: () => void;
  onBankPromoInteracted?: (cta: string, partner: string) => void;
}>;

export const AllyTransferPromo: React.FC<AllyTransferPromoProps> = ({ allyAmountFormatted, standardAmountFormatted, feeFormatted, onConnectBank, onBankPromoInteracted }) => {
  const { tq } = useTranslation('transferToBankDialog');
  const openWindow = useWindowFunction('open');
  const [chosen, setChosen] = useState<AllyTransferState>('ally');
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [allyOpened, setAllyOpened] = useState(false);

  const showingAlly = !allyOpened && ((chosen === 'ally' && !isPreviewing) || (chosen === 'existing' && isPreviewing));

  const handleOpenAccount = useCallback(() => {
    onBankPromoInteracted?.('open_account', 'ally');
    openWindow(ALLY_URL, '_blank', 'noopener,noreferrer');
    setAllyOpened(true);
  }, [onBankPromoInteracted, openWindow]);

  const handleChooseExisting = useCallback(() => {
    onBankPromoInteracted?.('choose_existing', 'ally');
    setChosen('existing');
    setIsPreviewing(false);
  }, [onBankPromoInteracted]);

  const handleClaimBonus = useCallback(() => {
    onBankPromoInteracted?.('claim_bonus', 'ally');
    openWindow(ALLY_URL, '_blank', 'noopener,noreferrer');
    setAllyOpened(true);
  }, [onBankPromoInteracted, openWindow]);

  const handleContinueWithExisting = useCallback(() => {
    onBankPromoInteracted?.('continue', 'ally');
    onConnectBank?.();
  }, [onBankPromoInteracted, onConnectBank]);

  const handleContinueAnyway = useCallback(() => {
    onBankPromoInteracted?.('continue_anyway', 'ally');
    onConnectBank?.();
  }, [onBankPromoInteracted, onConnectBank]);

  const handlePreview = useCallback(() => {
    setIsPreviewing(true);
  }, []);

  const handleResetPreview = useCallback(() => {
    setIsPreviewing(false);
  }, []);

  return (
    <Flex
      flexDirection="column"
      alignItems="center"
      textAlign="center"
      paddingX={7}
      paddingBottom={6}
    >
      {!allyOpened && (
        <>
          <AllyPromoHeadline marginBottom={3}>{tq`The right first step of your new life together.`}</AllyPromoHeadline>
          <AllyPromoSubtext marginBottom={8}>{tq`Joy will add an extra $200 when you open an account with Ally.`}</AllyPromoSubtext>
        </>
      )}

      <AllyAmountDisplay style={{ color: showingAlly ? ALLY_PURPLE : '#555' }}>{showingAlly ? allyAmountFormatted : standardAmountFormatted}</AllyAmountDisplay>
      <AllyAmountSubtext>{showingAlly ? tq`With Ally + $200 from Joy · ${feeFormatted} transfer fee` : tq`${feeFormatted} transfer fee`}</AllyAmountSubtext>

      {allyOpened ? (
        <AllyPrimaryButton
          type="button"
          size="lg"
          shape="rounded"
          backgroundColor={ALLY_PURPLE}
          color="white"
          _hover={{ backgroundColor: '#5a2475' }}
          onClick={handleContinueWithExisting}
        >
          {tq`Continue`}
        </AllyPrimaryButton>
      ) : chosen === 'existing' ? (
        <AllyNudgeBar width="100%">
          <AllyNudgeText>{tq`Just a heads up — your $200 gift from Joy & Ally won't be applied to your existing account.`}</AllyNudgeText>
          <Flex
            flexDirection="column"
            gap={2}
          >
            <ButtonV2
              type="button"
              fullWidth
              size="sm"
              shape="rounded"
              backgroundColor={ALLY_PURPLE}
              color="white"
              _hover={{ backgroundColor: '#5a2475' }}
              onMouseEnter={handlePreview}
              onMouseLeave={handleResetPreview}
              onClick={handleClaimBonus}
            >
              {tq`Claim my $200`}
            </ButtonV2>
            <ButtonV2
              type="button"
              fullWidth
              size="sm"
              shape="rounded"
              variant="outline"
              intent="neutral"
              onClick={handleContinueAnyway}
            >
              {tq`Continue anyway`}
            </ButtonV2>
          </Flex>
        </AllyNudgeBar>
      ) : (
        <>
          <AllyPrimaryButton
            type="button"
            size="lg"
            shape="rounded"
            backgroundColor={ALLY_PURPLE}
            color="white"
            _hover={{ backgroundColor: '#5a2475' }}
            onClick={handleOpenAccount}
          >
            {tq`Get $200 more towards your new life`}
          </AllyPrimaryButton>
          <AllySecondaryButton
            type="button"
            size="lg"
            shape="rounded"
            variant="outline"
            intent="neutral"
            onMouseEnter={handlePreview}
            onMouseLeave={handleResetPreview}
            onClick={handleChooseExisting}
          >
            {tq`I'll just use my existing account`}
          </AllySecondaryButton>
        </>
      )}

      {!allyOpened && (
        <AllyFinePrint>
          {tq`$200 bonus requires a $500 min. deposit within 30 days. `}
          <TextV2
            as="a"
            href={ALLY_URL}
            target="_blank"
            rel="noopener noreferrer"
            typographyVariant="label2"
            style={{ color: '#bbb', textDecoration: 'underline' }}
            onClick={() => {
              onBankPromoInteracted?.('open_account', 'ally');
              setAllyOpened(true);
            }}
          >
            {tq`Terms apply.`}
          </TextV2>
        </AllyFinePrint>
      )}
    </Flex>
  );
};
